import React from 'react';
import PropTypes from 'prop-types';

import {useMediaQuery, useTheme} from '@mui/material';
import {Alert, Box, IconButton, Stack, Typography, Avatar, Button, screenSizeSmaller} from 'views/components';
import {CheckCircleOutline, Close, ErrorOutline} from 'views/icons';
import {Translate} from 'views/module';

const HeaderSection = (props) => {
  const {
    questions,
    areAllSectionsExpanded,
    onToggleAllSections,
    caregiverRnOrShortId,
    clientRn,
  } = props;

  const [copyUrlAlert, setCopyUrlAlert] = React.useState({open: false, severity: 'success'});
  const [isCopying, setIsCopying] = React.useState(false);

  React.useEffect(() => {
    if (!copyUrlAlert.open) return;
    const timer = setTimeout(() => {
      setCopyUrlAlert((prev) => ({
        ...prev,
        open: false,
      }));
    }, 10000);
    return () => clearTimeout(timer);
  }, [copyUrlAlert]);

  const isSmUp = useMediaQuery(useTheme().breakpoints.up('sm'));
  const smallScreen = screenSizeSmaller('navbarHide');

  const name = `${questions.firstName} ${questions.lastName}`;
  const aboutName = questions.firstName || name;
  const initials = React.useMemo(() => {
    const firstInitial = (questions.firstName || '').trim().charAt(0);
    const lastInitial = (questions.lastName || '').trim().charAt(0);
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }, [questions.firstName, questions.lastName]);
  const personalBio = questions.personalBio;
  const motivation = questions.caregiverMotivation;
  const profilePhotoSrc = questions.profilePhoto?.[0]?.src;
  const city = questions.city;
  const province = questions.province;

  const copyProfileUrl = async () => {
    setIsCopying(true);
    try {
      const domain = process.env.REACT_APP_CLIENT_DOMAIN;
      // won't work on mobile if using local server but does appear to work in https contexts
      await window.navigator.clipboard.writeText(`${domain}/caregivers/${caregiverRnOrShortId}?client=${clientRn}`);
      setCopyUrlAlert({open: true, severity: 'success'});
    } catch (err) {
      setCopyUrlAlert({open: true, severity: 'error'});
    } finally {
      setTimeout(() => {
        setIsCopying(false);
      }, 1000);
    }
  };

  return (
    <Stack
      direction="column"
      spacing={{xs: 1, md: 2}}
      alignItems="stretch"
      width='100%'
      sx={{
        pt: {xs: 2, md: 4},
      }}
    >
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={{xs: 3, sm: 4}}
        alignItems={{xs: 'center', sm: 'flex-start'}}
        width="100%"
        sx={{pb: 2}}
      >
        {/* Profile Photo */}
        <Avatar
          src={profilePhotoSrc}
          alt={name}
          sx={{
            'width': {xs: 80, sm: 96, md: 128, lg: 152},
            'height': {xs: 80, sm: 96, md: 128, lg: 152},
            'border': '2px solid',
            'borderColor': 'color.divider',
            'bgcolor': 'primary.light',
            'background': 'linear-gradient(209.69deg, #DF7C58 9.27%, #FFD27E 81%)',
            'fontSize': 48,
            'fontWeight': 600,
            'lineHeight': '57px',
            'color': 'color.white',
            '& img': {
              overflowClipMargin: 'unset',
            },
          }}
        >
          {!profilePhotoSrc && initials}
        </Avatar>
        {/* Name and Share Profile */}
        <Stack
          direction={{xs: 'column', md: 'row'}}
          spacing={{xs: 1, md: 1.5}}
          alignItems={'center'}
          justifyContent="space-between"
          sx={{width: '100%'}}
        >
          <Stack spacing={{xs: 0.5, md: 1}} alignItems={{xs: 'center', md: 'start'}}>
            <Typography
              variant="h4"
              sx={{
                color: 'text.primary',
                fontWeight: 400,
                fontSize: 32,
                lineHeight: '43px',
              }}
            >
              {name}
            </Typography>
            {/* Location */}
            <Typography
              variant="subtitle1"
              sx={{
                color: '#686868',
                fontWeight: 510,
                fontSize: 16,
                lineHeight: '23px',
              }}
            >
              {city}{`, ${province}`}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              pt: {xs: 1, md: 0},
            }}
          >
            { isSmUp &&
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onToggleAllSections}
                  sx={{
                    'bgcolor': '#F9F1EA',
                    'borderColor': 'transparent',
                    'borderRadius': '9px',
                    'color': '#000000',
                    'px': 3,
                    'py': 2,
                    'fontWeight': 510,
                    'fontSize': 14,
                    'lineHeight': '17px',
                    'textAlign': 'center',
                    '&:hover': {
                      bgcolor: '#F9F1EA',
                      borderColor: 'transparent',
                    },
                  }}
                >
                  <Translate
                    expression={
                    areAllSectionsExpanded ?
                      'caregiverDetail.Header.CollapseAll' :
                      'caregiverDetail.Header.ExpandAll'
                    }
                  />
                </Button>
            }
            <Button
              variant="outlined"
              size="small"
              onClick={copyProfileUrl}
              sx={{
                'bgcolor': '#FFFFFF',
                'borderColor': '#DDD8D7',
                'borderRadius': '9px',
                'color': isCopying ? 'primary.main' : '#000000',
                'px': 3,
                'py': 2,
                'fontWeight': 510,
                'fontSize': 14,
                'lineHeight': '17px',
                'textAlign': 'center',
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  borderColor: '#DDD8D7',
                },
              }}
            >
              <Translate expression="caregiverDetail.Header.ShareProfile" />
            </Button>
          </Stack>
        </Stack>
      </Stack>
      {/* About */ }
      <Typography
        variant="body1"
        fontWeight="bold"
        sx={{
          color: '#686868',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        <Translate
          expression="caregiverDetail.Header.AboutTitle"
          substitute={{name: aboutName}}
        />
      </Typography>
      <Stack
        sx={{
          p: 2,
          bgcolor: 'color.white',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'color.divider',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {
          personalBio && (
            <Typography variant="body1" color="text.primary" sx={{pb: motivation ? 1 : 0}}>
              {personalBio}
            </Typography>
          )
        }
        {
          motivation && (
            <Typography variant="body1" color="text.primary" sx={{pb: 0.5}}>
              {motivation}
            </Typography>
          )
        }
      </Stack>
      {
        copyUrlAlert.open && (
          <Box
            sx={{
              position: 'fixed',
              top: {xs: 8, sm: 16},
              left: smallScreen ? 0 : 312,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              zIndex: 1400,
              pointerEvents: 'none',
            }}
          >
            <Alert
              icon={
                copyUrlAlert.severity === 'success' ?
                  <CheckCircleOutline fontSize="small"/> :
                  <ErrorOutline fontSize="small"/>
              }
              severity={
                copyUrlAlert.severity
              }
              action={
                <IconButton
                  size="small"
                  onClick={() => setCopyUrlAlert((prev) => ({
                    ...prev,
                    open: false,
                  }))}
                  sx={{color: 'grey.600'}}
                >
                  <Close fontSize="small"/>
                </IconButton>
              }
              sx={{
                'alignItems': 'center',
                'bgcolor': '#FFFDFC',
                'color': 'text.primary',
                'boxShadow': '0px 8px 24px rgba(0, 0, 0, 0.12)',
                'borderRadius': 4,
                'minWidth': {xs: '90%', sm: 'auto'},
                'pointerEvents': 'auto',
              }}
            >
              <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                <Translate
                  expression={
                    copyUrlAlert.severity === 'success' ?
                      'caregiverDetail.Header.CopyMessage' :
                      'caregiverDetail.Header.CopyErrorMessage'
                  }
                />
              </Typography>
            </Alert>
          </Box>
        )
      }
    </Stack>
  );
};

HeaderSection.propTypes = {
  questions: PropTypes.object.isRequired,
  areAllSectionsExpanded: PropTypes.bool,
  onToggleAllSections: PropTypes.func,
  caregiverRnOrShortId: PropTypes.string.isRequired,
  clientRn: PropTypes.string.isRequired,
};

export default HeaderSection;
